/** Attraction type options */
export const ATTRACTION_TYPES = ['cultural', 'natural', 'entertainment', 'religious', 'historical'] as const;
export type AttractionType = (typeof ATTRACTION_TYPES)[number];

/** Core attraction entity */
export interface Attraction {
  id: string;
  attractionId: string;
  name: string;
  description: string;
  city: string;
  state: string | null;
  province: string | null;
  country: string;
  countryCode: string;
  lePlaceId: string | null;
  lePlaceType: string | null;
  latitude: number;
  longitude: number;
  type: AttractionType;
  popularityRank: number;
  stateRank: number | null;
  provinceRank: number | null;
  cityRank: number | null;
  imageUrl: string | null;
  active: boolean;
  offerCount: number;
  manualRank?: number | null;
  spacingExcluded?: boolean;
  spacingExcludedBy?: string | null;
}

export type RankScopeType = 'country' | 'state' | 'province' | 'city';

export interface RankChange {
  attractionId: string;
  rank: number | null;
}

export interface SaveRankingsPayload {
  scopeType: RankScopeType;
  scopeValue: string;
  rankings: Array<RankChange>;
}

/** Response from image mutation endpoints (PATCH image, DELETE image, PATCH location-image). */
export interface AttractionImageMutationResponse {
  attractionId: string;
  imageUrl: string | null;
  imageCurated?: boolean;
  locationImageUrl?: string | null;
  /**
   * ISO-8601 timestamp used as CDN cache-buster on the next GET.
   * Optional: absent while the paired svc-experiences PR has not shipped.
   */
  updatedAt?: string;
}

/** City with attraction coverage data */
export interface CityWithCoverage {
  city: string;
  country: string;
  countryCode: string;
  lePlaceId: string | null;
  attractionCount: number;
  coveredCount: number;
  coverageRate: number;
}

/** Offer matched to an attraction */
export interface MatchedOffer {
  id: string;
  title: string;
  shortDescription: string;
  slug: string;
  provider: string;
  matchType: 'PROXIMITY' | 'DIRECT_TITLE' | 'KEYWORD' | 'AI_SEMANTIC' | 'MANUAL';
  relevanceScore: number;
  excluded: boolean;
  /** Present for curator roles when svc-experiences returns display_sequence. */
  displaySequence?: number | null;
  latitude: number | null;
  longitude: number | null;
}

/** Image available for an attraction from linked offers */
export interface AttractionImage {
  imageId: string;
  url: string;
  isHero: boolean;
  offerTitle: string;
  offerId: string;
}

/** Query params for listing attractions */
export interface ListAttractionsParams {
  city?: string;
  state?: string;
  province?: string;
  country?: string;
  countryCode?: string;
  type?: string;
  search?: string;
  sort?: string;
  limit?: number;
  offset?: number;
  includeInactive?: boolean;
  activeOnly?: boolean;
}

/** API response for a single attraction */
export interface GetAttractionResponse {
  result: Attraction;
}

/** API response for listing attractions */
export interface ListAttractionsResponse {
  result: Array<Attraction>;
  total: number;
}

/** API response for listing cities */
export interface ListAttractionCitiesResponse {
  result: Array<CityWithCoverage>;
}

/** API response for listing offers */
export interface ListAttractionOffersResponse {
  result: Array<MatchedOffer>;
  total: number;
}

/** API response for listing images */
export interface ListAttractionImagesResponse {
  result: Array<AttractionImage>;
}

/** Query params for listing attraction offers */
export interface ListAttractionOffersParams {
  limit?: number;
  offset?: number;
  excludedOnly?: boolean;
  search?: string;
}

/** City-level stats within a country group */
export interface CityGroupStats {
  city: string;
  attractionCount: number;
  coveredCount: number;
  coverageRate: number;
  totalOffers: number;
}

/** Country-level stats */
export interface CountryStats {
  countryCode: string;
  country: string;
  attractionCount: number;
  coveredCount: number;
  coverageRate: number;
  totalOffers: number;
  cityCount: number;
  cities?: Array<CityGroupStats>;
}

/** Aggregated stats result */
export interface StatsResult {
  totalAttractions: number;
  totalCovered: number;
  coverageRate: number;
  totalCountries: number;
  totalCities: number;
  totalOffers: number;
  countries: Array<CountryStats>;
}

/** Filter options for dropdowns */
export interface FilterOptions {
  states: Array<string>;
  provinces: Array<string>;
  cities: Array<string>;
}

/** API response for filter options endpoint */
export interface GetFilterOptionsResponse {
  result: FilterOptions;
}

/** API response for stats endpoint */
export interface GetAttractionStatsResponse {
  result: StatsResult;
}

/** Query params for stats endpoint */
export interface GetAttractionStatsParams {
  city?: string;
  countryCode?: string;
  type?: string;
  groupBy?: 'city';
}

export interface FilterOptionsParams {
  countryCode?: string;
  state?: string;
  province?: string;
  activeOnly?: boolean;
}

/** Performance analytics */
export interface AttractionPerformanceParams {
  from?: string;
  to?: string;
  countryCode?: string;
  name?: string;
  state?: string;
  province?: string;
  city?: string;
  type?: string;
  activeOnly?: boolean;
  sort?:
    | 'popularityRank'
    | 'offerImpressions'
    | 'offerClicks'
    | 'offerCtr'
    | 'name'
    | 'bookings'
    | 'revenue'
    | 'ttvAr'
    | 'margin'
    | 'marginPct'
    | 'offerCount';
  sortDirection?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export interface AttractionPerformanceSummary {
  carouselImpressions: number;
  tileImpressions: number;
  tileClicks: number;
  tileCtr: number;
  offerImpressions: number;
  offerClicks: number;
  offerCtr: number;
  bookings: number;
  revenue: number;
  margin: number;
  marginPct: number;
  updatedAt: string;
  scope?: {
    requestedScope: {
      countryCode?: string;
      name?: string;
      state?: string;
      province?: string;
      city?: string;
      type?: string;
      activeOnly: boolean;
    };
    metricScopes: {
      carouselImpressions: 'country';
      tileImpressions: 'country';
      tileClicks: 'country';
      offerImpressions: 'country' | 'filtered-attractions';
      offerClicks: 'country' | 'filtered-attractions';
      bookings: 'country' | 'filtered-attractions-attributed';
      revenue: 'country' | 'filtered-attractions-attributed';
      margin: 'country' | 'filtered-attractions-attributed';
      marginPct: 'country' | 'filtered-attractions-attributed';
    };
    warnings: Array<'carousel_tile_metrics_country_scoped' | 'financial_metrics_attribution_only' | 'metrics_unavailable'>;
  };
}

export interface AttractionPerformanceRow {
  attractionId: string;
  name: string;
  imageUrl: string | null;
  city: string;
  state: string | null;
  countryCode: string | null;
  type: string;
  active: boolean;
  offerCount: number;
  offerImpressions: number;
  offerClicks: number;
  offerCtr: number;
  bookings?: number;
  revenue?: number;
  ttvAr?: number;
  cost?: number;
  margin?: number;
  marginPct?: number;
}

export interface GetAttractionPerformanceResponse {
  result: Array<AttractionPerformanceRow>;
  total: number;
}

export interface GetAttractionPerformanceSummaryResponse {
  result: AttractionPerformanceSummary;
}

/** Formats attraction type enum for display (e.g. CULTURAL_SITE -> Cultural Site) */
export function formatAttractionType(type: string): string {
  return type
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}
